"use client";

import React from "react";
import { Icon } from "@iconify/react";
import { Spinner } from "@heroui/spinner";
import parse from "html-react-parser";
import { motion } from "framer-motion";

import useSingleBlog from "./useSingleBlog";

import { CommentSection, SimilarBlog, ViewImage } from "@/components";

const SingleBlog = () => {
  const { singleBlog, router, blogLoading, categories, handleShare } = useSingleBlog();

  const categoryName =
    categories?.find((cat: { _id: string }) => cat._id === singleBlog?.category)
      ?.name ?? "General";

  return (
    <div className="min-h-screen bg-background">
      {blogLoading ? (
        <div className="flex items-center justify-center h-screen">
          <Spinner size="lg" />
        </div>
      ) : singleBlog ? (
        <>
          {/* HERO */}
          <section className="relative overflow-hidden">
            <ViewImage
              className="h-[420px] md:h-[540px] w-full rounded-none object-cover"
              img={singleBlog?.featuredImage}
            />

            {/* overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/10" />

            {/* back button */}
            <div className="absolute top-6 left-6 z-20 lg:left-28 flex items-center gap-2">
              <button
                className="
                  flex items-center gap-2
                  rounded-full border border-white/20
                  bg-neutral-500 backdrop-blur-md
                  px-4 py-2 text-sm text-white
                  transition-all duration-300
                  hover:bg-white hover:text-black
                "
                onClick={() => router.push("/blog")}
              >
                <Icon className="text-lg" icon="ri:arrow-left-line" />
                <span>Back to blog</span>
              </button>
              <button
                onClick={handleShare}
                className="
                  flex items-center gap-2
                  rounded-full border border-white/20
                  bg-neutral-500 backdrop-blur-md
                  px-4 py-2 text-sm text-white
                  transition-all duration-300
                  hover:bg-white hover:text-black
                "
              >
                <Icon icon="solar:share-outline" />
                <span>Share</span>
              </button>
            </div>

            {/* hero content */}
            <div className="absolute inset-0 z-10 flex items-end ">
              <div className="w-full lg:px-28 px-4 pt-14 max-w-screen-2xl pb-14 text-white bg-gradient-to-b from-transparent via-black/50 to-black/50  ">
                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  initial={{ opacity: 0, y: 30 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <div className="rounded-full bg-primary dark:bg-primary-50 px-4 py-1 text-xs font-medium uppercase tracking-wider text-white">
                      {categoryName}
                    </div>

                    <div className="text-sm text-white">
                      {new Date(singleBlog?.createdAt as string).toDateString()}
                    </div>
                  </div>

                  <h1 className="text-lg md:text-xl lg:text-3xl font-black leading-tight max-w-4xl">
                    {singleBlog?.title}
                  </h1>
                </motion.div>
              </div>
            </div>
          </section>

          {/* CONTENT */}
          <section className="relative">
            <div className="max-w-4xl mx-auto px-6 lg:px-0">
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="
    relative -mt-10 z-20
    rounded-t-[32px]
    bg-background/95 backdrop-blur-xl
    p-6 md:p-10 lg:p-14

shadow-[0_-4px_30px_-12px_rgba(0,0,0,0.12)]
dark:shadow-[0_-4px_30px_-12px_rgba(0,0,0,0.45)]
  "
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                {/* top glow */}
                <div className="absolute inset-x-0 top-0 h-20 rounded-t-[32px] bg-white/20 dark:bg-white/[0.03] pointer-events-none" />

                {/* bottom fade */}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-background pointer-events-none" />

                <div
                  className="
    relative z-10
    prose prose-neutral dark:prose-invert
    max-w-none

  "
                >
                  {typeof singleBlog?.content === "string"
                    ? parse(singleBlog?.content)
                    : null}
                </div>
              </motion.div>
            </div>
            <div className="max-w-screen-2xl mx-auto lg:px-28 px-6">
              <CommentSection />
              <SimilarBlog />
            </div>
          </section>

        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-screen text-center px-6">
          <div className="w-20 h-20 rounded-full bg-default-100 flex items-center justify-center mb-5">
            <Icon
              className="text-4xl text-default-500"
              icon="solar:document-text-broken"
            />
          </div>

          <h2 className="text-2xl font-bold">Blog not found</h2>

          <p className="text-foreground-500 mt-2 max-w-md">
            The blog post you are looking for may have been removed or does not
            exist.
          </p>

          <button
            className="
              mt-6 rounded-full bg-primary
              px-6 py-3 text-sm font-semibold text-white
              transition-all duration-300 hover:scale-[1.03]
            "
            onClick={() => router.push("/blog")}
          >
            Go back to blogs
          </button>
        </div>
      )}
    </div>
  );
};

export default SingleBlog;
